import { AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import type { IncompleteMaterial } from '@/features/production-declaration/hooks/use-production-declaration-completeness';

type ProductionDeclarationCompletenessAlertProps = {
  incompleteMaterials: IncompleteMaterial[];
};

export function ProductionDeclarationCompletenessAlert({
  incompleteMaterials,
}: ProductionDeclarationCompletenessAlertProps) {
  const { t } = useTranslation('productionDeclaration', {
    useSuspense: false,
  });

  if (incompleteMaterials.length === 0) {
    return null;
  }

  return (
    <Alert variant='destructive'>
      <AlertCircle className='size-4' />
      <AlertTitle>{t('completeness.errorTitle')}</AlertTitle>
      <AlertDescription>
        <ul className='list-disc pl-4'>
          {incompleteMaterials.map((m) => (
            <li key={m.materialId}>
              <strong>{m.materialEnglishName}</strong>
              {m.hasMissingOrigins ? (
                <span>
                  {' — '}
                  {t('completeness.missingCommodityOrigin')}
                </span>
              ) : (
                <ul className='list-disc pl-4'>
                  {m.incompleteOrigins.map((co) => (
                    <li key={co.commodityOriginId}>
                      {t('completeness.originLabel', {
                        sequenceNumber: co.sequenceNumber,
                      })}
                      {' — '}
                      {[
                        !co.hasProductionPlaces &&
                          t('completeness.missingProductionPlaces'),
                        !co.hasSupplyChain &&
                          t('completeness.missingSupplyChain'),
                        !co.hasDocuments && t('completeness.missingDocuments'),
                      ]
                        .filter(Boolean)
                        .join(', ')}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      </AlertDescription>
    </Alert>
  );
}
